import { Button } from '@thanx/uikit/button'
import imgDiscountRosette from 'assets/images/recovery-ai/img_discount_rosette.svg'
import imgPromoConversation from 'assets/images/recovery-ai/img_promo_conversation.png'
import imgWordmark from 'assets/images/recovery-ai/img_wordmark.svg'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { feedbackProviderName } from '../feedbackOwnership'
import type { OverviewContent } from './resolveOverviewState'
import {
  OVERVIEW_CHECK_COLOR,
  OVERVIEW_PROMO_CARD_BORDER,
  OVERVIEW_PROMO_INSET_RULE,
  OVERVIEW_SETUP_CARD_PADDING_X,
  OVERVIEW_SETUP_CARD_PADDING_Y,
  OVERVIEW_TEXT_LINK_STYLE,
} from './sectionLayout'

const t = buildTranslate('order_feedback')

// Measured from the prototype. nephritis20 (#d6f5e4) is close to the pill
// background but not equal, and there is no token at all for the text green,
// so both are named here rather than rounded to a near-miss. The band's own
// fill IS a token — palette.spearmint10 — so it is used directly below.
const PILL_BACKGROUND = '#d9f2e0'
const PILL_TEXT = '#1e7a3d'

// The frame the two-phone screenshot fills. 210 x 128.5 is what the design's
// 505x309 asset measures at this column width, so the two agree to a
// hundredth of a pixel and the image is never distorted by being stretched to
// fit. Declared rather than left to the image's own aspect ratio so the
// band's geometry survives a future asset swap: a replacement at a different
// ratio is then cropped, not allowed to resize the band.
const PROMO_IMAGE_WIDTH = '210px'
const PROMO_IMAGE_HEIGHT = '128.5px'

// Where the promo sits. 'inset' is the design's band bled to the setup card's
// own edges — it cancels that card's bottom padding and reaches its corners,
// so the two read as a single card. 'standalone' is the roomier separate card
// the design uses only where a third-party tool is already collecting, and a
// compact status banner rather than a setup card sits above it.
export type PromoVariant = 'inset' | 'standalone'

type Props = {
  content: OverviewContent
  variant: PromoVariant
  onCta?: () => void
}

// The design's bullet glyph: a check, not a disc. Copied from the prototype
// rather than redrawn, and decorative — a bullet's meaning is its text.
const CheckMark = ({
  size,
  className,
}: {
  size: number
  className: string
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={OVERVIEW_CHECK_COLOR}
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

// The RecoveryAI promotional block. Two bullets where RecoveryAI is pitched
// on its own, three where it is pitched as running alongside an existing
// third-party tool. Hidden once RecoveryAI covers every digital order.
//
// The design sets both headings as the RecoveryAI wordmark followed by the
// copy, so each state's heading string is only the trailing half of the
// sentence ("responds for you") and is a fragment on its own — the wordmark
// carries the subject. It carries it to a screen reader too, via its alt
// text, so the heading's accessible name is still the whole sentence. The two
// variants set the mark at the two heights the design gives it.
const PromoBlock = ({ content, variant, onCta }: Props) => {
  const [css] = useStyletron()
  const { promo, thirdPartyProvider } = content

  if (!promo.visible) return null

  const providerName = feedbackProviderName(thirdPartyProvider)

  const wordmark = (height: string) => (
    <img
      src={imgWordmark}
      alt={t('promo_logo_alt')}
      className={css({ height, width: 'auto', display: 'block' })}
    />
  )

  const pill = css({
    fontSize: '10.5px',
    fontWeight: 800,
    lineHeight: '15.75px',
    letterSpacing: '0.42px',
    textTransform: 'uppercase',
    color: PILL_TEXT,
    backgroundColor: PILL_BACKGROUND,
    padding: '4px 10px',
    borderRadius: '20px',
  })

  const footerRow = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    flexWrap: 'wrap',
  })

  if (variant === 'standalone') {
    return (
      <div
        className={css({
          padding: '28px 30px',
          backgroundColor: palette.spearmint10,
          border: `1px solid ${OVERVIEW_PROMO_CARD_BORDER}`,
          borderRadius: '14px',
        })}
      >
        {promo.headingKey && (
          <h3
            className={css({
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
              fontSize: '19px',
              fontWeight: 800,
              lineHeight: 1.3,
              color: palette.grey90,
              margin: '0 0 16px',
            })}
          >
            {wordmark('17px')}
            <span>{t(promo.headingKey)}</span>
          </h3>
        )}

        <ul
          className={css({
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            margin: '0 0 20px',
            padding: 0,
          })}
        >
          {promo.bulletKeys.map(key => (
            <li
              key={key}
              className={css({
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                fontSize: '14.5px',
                fontWeight: 500,
                lineHeight: 1.4,
                color: palette.grey90,
              })}
            >
              <CheckMark
                size={16}
                className={css({ flexShrink: 0, marginTop: '2px' })}
              />
              <span>{t(key, { provider: providerName })}</span>
            </li>
          ))}
        </ul>

        <div className={footerRow}>
          {promo.badgeKey && <span className={pill}>{t(promo.badgeKey)}</span>}
          {promo.ctaKey && (
            <Button kind="primary" onClick={() => onCta?.()}>
              {t(promo.ctaKey)}
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={css({
        backgroundColor: palette.spearmint10,
        borderTop: `1px solid ${OVERVIEW_PROMO_INSET_RULE}`,
        // The setup card's own 14px radius less its 1.5px border.
        borderRadius: '0 0 13px 13px',
        padding: `18px ${OVERVIEW_SETUP_CARD_PADDING_X}px 20px`,
        margin: `18px -${OVERVIEW_SETUP_CARD_PADDING_X}px -${OVERVIEW_SETUP_CARD_PADDING_Y}px`,
      })}
    >
      {promo.headingKey && (
        <h3
          className={css({
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px',
            fontSize: '16px',
            fontWeight: 800,
            lineHeight: 1.3,
            color: palette.grey90,
            margin: 0,
          })}
        >
          {wordmark('15px')}
          <span>{t(promo.headingKey)}</span>
        </h3>
      )}

      <div
        className={css({
          display: 'flex',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '20px',
          marginTop: '14px',
        })}
      >
        {/* The two-phone screenshot of a RecoveryAI conversation. Described
            rather than hidden, following the design: it is the one thing in
            the band that shows the product doing the work, and the bullets
            beside it qualify that claim rather than restate it. */}
        <div
          className={css({
            flexShrink: 0,
            width: PROMO_IMAGE_WIDTH,
            height: PROMO_IMAGE_HEIGHT,
            borderRadius: '12px',
            overflow: 'hidden',
            lineHeight: 0,
          })}
        >
          <img
            src={imgPromoConversation}
            alt={t('promo_conversation_alt')}
            className={css({
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            })}
          />
        </div>

        <div className={css({ flex: '1 1 0%', minWidth: 0 })}>
          <ul
            className={css({
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '7px',
              margin: '0 0 15px',
              padding: 0,
            })}
          >
            {promo.bulletKeys.map(key => (
              <li
                key={key}
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '21px',
                  color: palette.grey90,
                })}
              >
                <CheckMark size={15} className={css({ flexShrink: 0 })} />
                <span>{t(key, { provider: providerName })}</span>
              </li>
            ))}
          </ul>

          <div className={footerRow}>
            <span
              className={css({
                display: 'inline-flex',
                alignItems: 'center',
                gap: '9px',
              })}
            >
              {/* The discount rosette that marks the pill. Decorative — the
                  pill it sits against carries the words. Drawn at 22px from a
                  20px artboard, which is what the design does. */}
              <img
                src={imgDiscountRosette}
                alt=""
                className={css({
                  flexShrink: 0,
                  width: '22px',
                  height: '22px',
                  display: 'block',
                })}
              />
              {promo.badgeKey && (
                <span className={pill}>{t(promo.badgeKey)}</span>
              )}
            </span>
          </div>
        </div>
      </div>

      {promo.ctaKey && (
        <div
          className={css({
            textAlign: 'right',
            lineHeight: '24px',
            marginTop: '14px',
          })}
        >
          <button
            type="button"
            className={css(OVERVIEW_TEXT_LINK_STYLE)}
            onClick={() => onCta?.()}
          >
            {t(promo.ctaKey)}
          </button>
        </div>
      )}
    </div>
  )
}

export default PromoBlock
